import type {FC} from 'react'
import {useMemo, useRef} from 'react'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {Blankslate} from '../Blankslate'
import {ActorBypassMode, type BypassActor} from '@github-ui/bypass-actors/types'
import {BypassList} from '@github-ui/bypass-actors/BypassList'
import {BypassSelectPanel} from '@github-ui/bypass-actors/BypassSelectPanel'
import type {RulesetRoutePayload, RulesetTarget} from '../../types/rules-types'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'
import {useRuleStrings} from '../../hooks/use-rule-strings'

import {capitalize} from '../../helpers/string'
import {PanelHeader} from './conditions/PanelHeader'

import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'

export type BypassListPanelProps = {
  readOnly?: boolean
  bypassActors: BypassActor[]
  setBypassActors: (bypassActors: BypassActor[]) => void
  addBypassActor: (
    actorId: BypassActor['actorId'],
    actorType: BypassActor['actorType'],
    preferredAvatarUrl: BypassActor['preferredAvatarUrl'],
    name: BypassActor['name'],
    bypassMode: BypassActor['bypassMode'],
    owner: BypassActor['owner'],
  ) => void
  rulesetTarget: RulesetTarget
  removeBypassActor: (bypassActor: BypassActor) => void
  updateBypassActor: (bypassActor: BypassActor) => void
}

export const BypassListPanel: FC<BypassListPanelProps> = ({
  readOnly,
  bypassActors,
  addBypassActor,
  removeBypassActor,
  rulesetTarget,
  updateBypassActor,
}: BypassListPanelProps) => {
  const {baseAvatarUrl, sourceType} = useRoutePayload<RulesetRoutePayload>()
  const {resolvePath} = useRelativeNavigation()
  const {rulesetOrPolicy} = useRuleStrings()
  const exemptEnabled = useFeatureFlag('ruleset_configure_skip_actors')

  const supportedBypassModes: ActorBypassMode[] =
    rulesetTarget === 'branch' ? [ActorBypassMode.ALWAYS, ActorBypassMode.PRS_ONLY] : [ActorBypassMode.ALWAYS]

  if (exemptEnabled) {
    supportedBypassModes.push(ActorBypassMode.EXEMPT)
  }

  const bypassSuggestionsUrl = resolvePath('bypass_suggestions')

  const enabledBypassActors = useMemo(() => {
    return [
      ...bypassActors
        .filter(({_enabled}) => _enabled)
        .sort(a => (a.name === 'admin' && a.actorType === 'RepositoryRole' ? -1 : 0)),
    ]
  }, [bypassActors])

  const bypassSelectPanelAnchorRef = useRef<HTMLButtonElement>(null)

  let listName = 'bypass list'
  let bypassTitle = 'Add bypass'
  let reviewerGroup = 'roles, teams, and apps'
  if (rulesetTarget === 'repository') {
    listName = 'allow list'
    bypassTitle = 'Add actors'
  }
  if (sourceType === 'enterprise') {
    // Temporarily add 'enterprise' condition
    // until we support apps and teams at the enterprise level
    reviewerGroup = 'roles'
  }

  let subtext = `Exempt ${reviewerGroup} from this ${rulesetOrPolicy} by adding them to the ${listName}.`

  if (rulesetTarget === 'push') {
    // Temporarily add 'enterprise' condition
    // until we support apps and teams at the enterprise level
    if (sourceType === 'enterprise') {
      subtext = `Select the roles that can bypass and also approve bypass requests.`
    } else {
      subtext = `Select the roles and teams that can bypass and also approve bypass requests. Add the bots that can bypass this ${rulesetOrPolicy}.`
    }
  }

  return (
    <>
      <div className="mb-3">
        <PanelHeader
          title={`${capitalize(listName)}`}
          subtitle={subtext}
          trailing={
            !readOnly && (
              <div className="ml-auto">
                <BypassSelectPanel
                  title={bypassTitle}
                  baseAvatarUrl={baseAvatarUrl}
                  enabledBypassActors={enabledBypassActors}
                  addBypassActor={addBypassActor}
                  removeBypassActor={removeBypassActor}
                  suggestionsUrl={bypassSuggestionsUrl}
                  addReviewerSubtitle={`Choose which ${reviewerGroup} can bypass this ${rulesetOrPolicy}`}
                  bypassSelectPanelAnchorRef={bypassSelectPanelAnchorRef}
                />
              </div>
            )
          }
        />
      </div>
      <div className="Box">
        {enabledBypassActors.length > 0 ? (
          <ul>
            <BypassList
              readOnly={readOnly}
              removeBypassActor={removeBypassActor}
              refocusOnRemoval={() =>
                setTimeout(() => {
                  bypassSelectPanelAnchorRef.current?.focus()
                })
              }
              supportedBypassModes={supportedBypassModes}
              updateBypassActor={updateBypassActor}
              enabledBypassActors={enabledBypassActors}
              baseAvatarUrl={baseAvatarUrl}
            />
          </ul>
        ) : (
          <Blankslate heading={`${capitalize(listName)} is empty`} />
        )}
      </div>
    </>
  )
}

try{ BypassListPanel.displayName ||= 'BypassListPanel' } catch {}